import React, { useContext, useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import ChatiaPopover, { PopoverOptionItem } from "../ChatiaPopover";

const useStyles = makeStyles(() => ({
  triggerButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 8,
    borderRadius: 6,
    transition: "all 0.15s ease",
    "&:hover": {
      background: "rgba(255,255,255,0.15)",
    },
  },
  triggerFlag: {
    width: 28,
    height: 20,
    objectFit: "cover",
    borderRadius: 3,
    border: "1.5px solid rgba(255,255,255,0.3)",
    display: "block",
  },
}));

// Catálogo único — adicionar idioma novo aqui também requer arquivo
// correspondente em frontend/translate/languages/ e flag em /flags/.
const ALL_LANGUAGES = [
  { code: "pt-BR", shortCode: "pt", flag: "/flags/br.png", name: "Português" },
  { code: "en", shortCode: "en", flag: "/flags/us.png", name: "English" },
  { code: "es", shortCode: "es", flag: "/flags/es.png", name: "Español" },
  { code: "tr", shortCode: "tr", flag: "/flags/tr.png", name: "Türkçe" },
  { code: "ar", shortCode: "ar", flag: "/flags/sa.png", name: "العربية" },
];

const UserLanguageSelector = () => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const { user } = useContext(AuthContext);
  const [companyDefaultLang, setCompanyDefaultLang] = useState("pt-BR");
  const [companyAvailableLangs, setCompanyAvailableLangs] = useState(
    ALL_LANGUAGES.map((l) => l.code)
  );
  // Força re-render no languageChanged pra refletir a seleção sem reload.
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const onLangChanged = () => forceUpdate((n) => n + 1);
    i18n.on("languageChanged", onLangChanged);
    return () => i18n.off("languageChanged", onLangChanged);
  }, []);

  // Buscar settings da empresa e aplicar idioma correto do BANCO
  useEffect(() => {
    if (!user?.companyId) return;
    let cancelled = false;

    (async () => {
      try {
        const { data: cs } = await api.get(`/companySettings/${user.companyId}`);
        if (cancelled) return;
        const defLang = cs?.defaultLanguage || "pt-BR";
        setCompanyDefaultLang(defLang);
        if (cs?.availableLanguages) {
          setCompanyAvailableLangs(
            cs.availableLanguages.split(",").filter(Boolean)
          );
        }

        // user.language no banco é a fonte de verdade:
        // - igual ao default da empresa → usuário segue o padrão
        // - diferente → usuário escolheu manualmente, respeitar
        const userLang = user?.language;
        const langToApply = !userLang || userLang === defLang ? defLang : userLang;

        // Aplicar apenas se diferente — evita loop com listener de languageChanged
        const currentShort = (i18n.language || "").split("-")[0];
        const targetShort = (langToApply || "").split("-")[0];
        if (i18n.language !== langToApply && currentShort !== targetShort) {
          i18n.changeLanguage(langToApply);
        }
        try {
          localStorage.setItem("i18nextLng", langToApply);
        } catch {}
      } catch {}
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.companyId, user?.language]);

  // Escutar mudanças em tempo real do Whitelabel (sem reload)
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.defaultLanguage) {
        setCompanyDefaultLang(e.detail.defaultLanguage);
      }
      if (e.detail?.availableLanguages) {
        const langs = Array.isArray(e.detail.availableLanguages)
          ? e.detail.availableLanguages
          : e.detail.availableLanguages.split(",").filter(Boolean);
        setCompanyAvailableLangs(langs);
      }
    };
    window.addEventListener("chatia:company-languages", handler);
    return () => window.removeEventListener("chatia:company-languages", handler);
  }, []);

  // Filtrar e ordenar: idioma padrão da empresa no topo
  const languageOptions = ALL_LANGUAGES
    .filter((l) => companyAvailableLangs.includes(l.code))
    .sort((a, b) => {
      if (a.code === companyDefaultLang) return -1;
      if (b.code === companyDefaultLang) return 1;
      return 0;
    });

  const currentLanguage = i18n.language || "pt";
  const currentFull =
    ALL_LANGUAGES.find((l) => l.shortCode === currentLanguage)?.code ||
    currentLanguage;

  const selectedLanguage =
    languageOptions.find(
      (lang) => lang.code === currentFull || lang.shortCode === currentLanguage
    ) || languageOptions[0] || ALL_LANGUAGES[0];

  const isSelected = (lang) =>
    lang.code === currentFull || lang.shortCode === currentLanguage;

  const headerLabel = (() => {
    const l = (currentLanguage || "pt").split("-")[0];
    return l === "en"
      ? "Language"
      : l === "es"
      ? "Idioma"
      : l === "tr"
      ? "Dil"
      : l === "ar"
      ? "اللغة"
      : "Idioma";
  })();

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleChangeLanguage = async (language) => {
    try {
      // Cache local + persistência no banco (fonte de verdade)
      try {
        localStorage.setItem("i18nextLng", language);
      } catch {}
      if (user && user.id) {
        await api.put(`/users/${user.id}`, { language });
      }
      // Reload garante que TODOS os componentes peguem o novo idioma
      window.location.reload();
    } catch (err) {
      toastError(err);
      handleClose();
    }
  };

  if (!selectedLanguage) return null;

  return (
    <>
      <div className={classes.triggerButton} onClick={handleOpen}>
        <img
          src={selectedLanguage.flag}
          alt={selectedLanguage.name}
          className={classes.triggerFlag}
        />
      </div>

      <ChatiaPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        title={headerLabel}
      >
        {languageOptions.map((lang) => (
          <PopoverOptionItem
            key={lang.code}
            icon={lang.flag}
            label={lang.name}
            selected={isSelected(lang)}
            onClick={() => handleChangeLanguage(lang.code)}
          />
        ))}
      </ChatiaPopover>
    </>
  );
};

export default UserLanguageSelector;
